export const TITLE_MAX_LENGTH = 120;
export const DESCRIPTION_MAX_LENGTH = 2000;
export const DESCRIPTION_MAX_RAW_LENGTH = 3000;
export const QUESTION_TEXT_MAX_LENGTH_COI = 180;
export const QUESTION_TEXT_MAX_LENGTH_SPEAKUP = 120;
export const QUESTION_TEXT_MAX_LENGTH = QUESTION_TEXT_MAX_LENGTH_COI; // For backward compatibility
export const QUESTION_DESCRIPTION_MAX_LENGTH = 2000;
export const QUESTION_DESCRIPTION_MAX_RAW_LENGTH = 3000;
export const OPTION_TEXT_MAX_LENGTH = 50;
export const CATEGORY_DESCRIPTION_MAX_LENGTH = 300;
export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 50;
export const MAX_DATA_GRID_COLUMNS = 10;

const STARTS_WITH_SPECIAL_CHAR = /^[%$#@!^&*+=[\]{}|\\:;"<>?/~`-]/;

import { stripHtml } from 'src/utils/stripHtml';
export { stripHtml };

export const trimWhitespace = (value: string): string => {
  return value.replace(/^\s+|\s+$/g, '');
};

export const collapseWhitespace = (value: string): string => {
  return value.trim().replace(/\s+/g, ' ');
};

export const getTrimmedLength = (value: string): number => {
  return value.trim().length;
};

export const getStrippedHtmlLength = (value: string): number => {
  return stripHtml(value).trim().length;
};

export const getRawHtmlLength = (value: string): number => {
  return value.trim().length;
};

const validateTextWithSpecialCharCheck = (
  value: string,
  maxLength: number,
  fieldName: string,
): string | null => {
  if (!value || getTrimmedLength(value) === 0) {
    return `${fieldName} cannot be empty or contain only whitespace`;
  }

  if (getTrimmedLength(value) > maxLength) {
    return `${fieldName} cannot exceed ${maxLength} characters`;
  }

  const trimmedValue = value.trim();
  if (STARTS_WITH_SPECIAL_CHAR.test(trimmedValue)) {
    return `${fieldName} cannot start with special characters`;
  }

  return null;
};

export const validateTitle = (value: string): string | null => {
  return validateTextWithSpecialCharCheck(
    collapseWhitespace(value),
    TITLE_MAX_LENGTH,
    'Title',
  );
};

export const validateDescription = (value: string): string | null => {
  if (getStrippedHtmlLength(value) > DESCRIPTION_MAX_LENGTH) {
    return `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`;
  }
  if (getRawHtmlLength(value) > DESCRIPTION_MAX_RAW_LENGTH) {
    return `Description has too much formatting - reduce formatting so it doesn't exceed ${DESCRIPTION_MAX_RAW_LENGTH} characters`;
  }
  return null;
};

export const validateOptionText = (
  value: string,
  fieldName: string = 'Option text',
): string | null => {
  if (!value || getTrimmedLength(value) === 0) {
    return `${fieldName} cannot be empty`;
  }
  if (getTrimmedLength(value) > OPTION_TEXT_MAX_LENGTH) {
    return `${fieldName} cannot exceed ${OPTION_TEXT_MAX_LENGTH} characters`;
  }
  return null;
};

export const validateQuestionText = (
  value: string,
  isCoI?: boolean,
): string | null => {
  const maxLength = isCoI
    ? QUESTION_TEXT_MAX_LENGTH_COI
    : QUESTION_TEXT_MAX_LENGTH_SPEAKUP;
  return validateTextWithSpecialCharCheck(value, maxLength, 'Question text');
};

export const validateQuestionDescription = (value: string): string | null => {
  if (getStrippedHtmlLength(value) > QUESTION_DESCRIPTION_MAX_LENGTH) {
    return `Question description cannot exceed ${QUESTION_DESCRIPTION_MAX_LENGTH} characters`;
  }
  if (getRawHtmlLength(value) > QUESTION_DESCRIPTION_MAX_RAW_LENGTH) {
    return `Question description has too much formatting - reduce formatting so it doesn't exceed ${QUESTION_DESCRIPTION_MAX_RAW_LENGTH} characters`;
  }
  return null;
};

export const validateCategoryName = (value: string): string | null => {
  if (!value || value.trim() === '') {
    return 'Category name is required';
  }
  return null;
};

export const validateCategoryDescription = (_value: string): string | null => {
  return null;
};

export const validateNumericValue = (
  value: string | undefined,
  otherValue: string | undefined,
  isTouched: boolean | undefined,
  type: 'min' | 'max',
): string | null => {
  if (!isTouched) return null;

  if (!value || value.trim() === '') {
    return `${type === 'min' ? 'Minimum' : 'Maximum'} value is required`;
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return 'Must be a valid number';
  }

  if (otherValue && otherValue.trim() !== '') {
    const otherNum = parseFloat(otherValue);
    if (!isNaN(otherNum)) {
      if (type === 'min' && numValue > otherNum) {
        return 'Must be less than or equal to maximum value';
      }
      if (type === 'max' && numValue < otherNum) {
        return 'Must be greater than or equal to minimum value';
      }
    }
  }

  return null;
};
